import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  // `payload` carries the consent package: dob, the eight required boxes, and the optional
  // marketing boxes. The server rejects the signup outright if any required box is missing.
  const register = async (email, password, first_name, last_name, phone, sms_consent = false, payload = {}) => {
    const { data } = await api.post("/auth/register", {
      email, password, first_name, last_name, phone,
      sms_consent: !!sms_consent,
      dob: payload.dob || "",
      marketing_email: !!payload.marketing_email,
      consent: payload.consent || {},
    });
    setUser(data);
    return data;
  };

  const guestSession = async (email, first_name, last_name) => {
    const { data } = await api.post("/auth/guest-session", { email, first_name, last_name });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, guestSession, logout, checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
